import React, { useState, useEffect } from 'react';
import { X, Trash2, BookOpen, AlertCircle, Maximize2, Minimize2, Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '../api';

const MyEnrollmentsModal = ({ onClose, onUpdate }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [enrolledRes, pendingRes] = await Promise.all([
        api.get('diplomas/?enrolled=true'),
        api.get('diplomas/?pending=true')
      ]);
      setEnrollments(enrolledRes.data);
      setPendingRequests(pendingRes.data);
    } catch (err) {
      console.error('Error fetching enrollments/requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async (diplomaId, diplomaName, isRequest = false) => {
    const actionText = isRequest ? 'cancelar la solicitud de' : 'desanotarte de';
    if (window.confirm(`¿Seguro que deseas ${actionText} "${diplomaName}"?`)) {
      try {
        await api.post(`diplomas/${diplomaId}/unenroll/`);
        await fetchData();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error('Error in unenrollment:', err);
        alert('No se pudo procesar la solicitud. Por favor, intente de nuevo.');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PE': return <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 d-flex align-items-center gap-1"><Clock size={12}/> Pendiente</span>;
      case 'RE': return <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 d-flex align-items-center gap-1"><XCircle size={12}/> Rechazada</span>;
      case 'AP': return <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 d-flex align-items-center gap-1"><CheckCircle size={12}/> Aprobada</span>;
      default: return null;
    }
  }

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className={`modal-dialog modal-dialog-centered ${isFullscreen ? 'modal-fullscreen' : 'modal-lg'}`}>
        <div className="modal-content glassmorphism border-0 shadow-lg">
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0">Mis Inscripciones</h5>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen(f => !f)}
                title={isFullscreen ? 'Contraer' : 'Ampliar a pantalla completa'}
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body p-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2 text-muted small">Cargando inscripciones...</p>
              </div>
            ) : (enrollments.length > 0 || pendingRequests.length > 0) ? (
              <div className="table-responsive">
                <table className="table table-hover premium-table align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Diplomatura</th>
                      <th>Municipio</th>
                      <th className="text-center">Estado</th>
                      <th className="text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e) => (
                      <tr key={`enrolled-${e.id}`}>
                        <td><h6 className="mb-0 fw-bold">{e.name}</h6></td>
                        <td><small className="text-muted">{e.municipio}</small></td>
                        <td className="text-center">{getStatusBadge('AP')}</td>
                        <td className="text-center">
                          <button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleUnenroll(e.id, e.name)}>
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {pendingRequests.map((r) => (
                      <tr key={`pending-${r.id}`}>
                        <td><h6 className="mb-0 fw-bold text-muted">{r.name}</h6></td>
                        <td><small className="text-muted">{r.municipio}</small></td>
                        <td className="text-center">{getStatusBadge(r.status || 'PE')}</td>
                        <td className="text-center">
                          <button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleUnenroll(r.id, r.name, true)}>
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <AlertCircle size={48} className="mb-3 opacity-50" />
                <p>No estás inscripto ni tienes solicitudes pendientes.</p>
              </div>
            )}
            <div className="mt-4">
              <button className="btn btn-primary w-100 py-2" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyEnrollmentsModal;
